import { Injectable, ComponentRef, Type, ViewContainerRef, EnvironmentInjector, createComponent } from '@angular/core';
import e from 'express';

const DEF_TTL_MINS = 1 * 60 * 1000;

/*
 */
interface CachedComponent<T> {
  ref: ComponentRef<T>;
  destroyed: boolean;
  expiresAt: number; // ms
}
/*
 */
@Injectable({ providedIn: 'root' })
export class ComponentCacheService {
  private _TAG = `[***] ${this.constructor.name}`;
  private readonly cache = new Map<string, CachedComponent<any>>();

  /*
   */
  public is_valid = (ref: CachedComponent<any>) =>
    !!ref?.ref && !ref.ref.hostView?.destroyed && ref.expiresAt > Date.now();

  /*
   */
  public get(key: string): ComponentRef<any> | undefined {
    const cached = this.cache.get(key);

    if (!cached) {
      return undefined;
    }

    if (!this.is_valid(cached)) {
      cached.ref.destroy();
      this.cache.delete(key);
      return undefined;
    }

    return cached.ref;
  }

  /*
   */
  public has(key: string): boolean {
    return !!this.get(key);
  }

  /*
   */
  public destroy(ref: ComponentRef<any>) {
    ref.destroy();
  }

  /*
   */
  public delete(key: string): void {
    const cached = this.cache.get(key);
    if (cached) {
      cached.ref.destroy();
      this.cache.delete(key);
    }
  }

  /* So. It's not clear!
     Here we r just DETACHING container from HOST.
     But it's STILL IN DOM and RAM!
  */
  public detach(container: ViewContainerRef, exclude?: number): void {
    if (!container) return;
    for (let i = container.length - 1; i >= 0; i--) {
      if (i === exclude) continue;
      container.get(i)?.destroy(); // for now with no cache
      // container.detach(i);
    }
  }

  /*
   */
  public clear(): void {
    for (const [key, cached] of this.cache.entries()) {
      this.destroyAndRemove(key, cached);
    }
  }

  /*
   */
  public keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /*
   */
  public size(): number {
    return this.cache.size;
  }

  /*
   */
  public cleanupExpired(): void {
    const now = Date.now();
    for (const [key, cached] of this.cache.entries()) {
      if (cached.expiresAt <= now) {
        this.destroyAndRemove(key, cached);
      }
    }
  }

  /*
   */
  private destroyAndRemove(key: string, cached: CachedComponent<any>): void {
    if (!cached.destroyed) {
      try {
        cached.ref.destroy();
      } catch (e) {
        console.warn(`${this._TAG} Failed to destroy component: ${key}`, e);
      }
      cached.destroyed = true;
    }
    this.cache.delete(key);
  }

  /* O_O
   */
  public initComponent<T>(
    key: string,
    component: Type<T>,
    container: ViewContainerRef | null,
    envInjector: EnvironmentInjector,
    opts: Record<string, any>,
    ttl: number = DEF_TTL_MINS,
  ): ComponentRef<T> {
    // const cached = this.cache.get(key);
    // if (cached) {
    //   const isViewDestroyed = (cached.ref.hostView as any)?.destroyed;

    //   if (isViewDestroyed || !this.is_valid(cached)) {
    //     console.warn(`${this._TAG} Invalid or destroyed view for '${key}'. Cleaning up...`);
    //     this.destroyAndRemove(key, cached);
    //   } else {
    //     try {
    //       Object.assign(cached.ref.instance, opts);
    //       console.debug(`${this._TAG} @@@ Using cached component '${key}'`);
    //       return cached.ref as ComponentRef<T>;
    //     } catch (e) {
    //       console.warn(`${this._TAG} Failed to re-insert cached component '${key}'. Destroying...`, e);
    //       this.destroyAndRemove(key, cached);
    //     }
    //   }
    // }

    // // Create new instance
    const ref = createComponent<T>(component, { environmentInjector: envInjector });
    (ref.hostView as any)?.rootNodes?.[0]?.setAttribute?.('data-uid', `uid-${Math.random().toString(36).slice(2)}`);

    const isInsertedAt = container?.indexOf(ref.hostView);
    if (container && isInsertedAt === -1) {
      container.insert(ref.hostView);
    }

    Object.assign(ref.instance, opts);
    // this.cache.set(key, { ref, destroyed: false, expiresAt: Date.now() + ttl });
    console.debug(`${this._TAG} Creating component '${key}'`);
    return ref;
  }

  /* Ciao. Next methods works only in Chrome with --enable-precise-memory-info or DEVTOOLS opened
   */
  public getStats(): { count: number; estimatedSizeBytes: number } {
    let size = 0;

    for (const { ref } of this.cache.values()) {
      const instance = ref?.instance;
      if (instance) {
        try {
          const str = JSON.stringify(instance, (key, value) => {
            if (typeof value === 'object' && value !== null) {
              if ('hostView' in value || 'changeDetectorRef' in value) {
                return undefined;
              }
            }
            return value;
          });
          size += str.length;
        } catch {
          size += 1000; // at least something ))
        }
      }
    }

    return {
      count: this.cache.size,
      estimatedSizeBytes: size,
    };
  }

  /*
   */
  public getStatsWithHeap(): {
    count: number;
    estimatedSizeBytes: number;
    heapUsedBytes?: number;
    heapTotalBytes?: number;
    heapLimitBytes?: number;
  } {
    const basicStats = this.getStats();
    const memory = (performance as any).memory;

    return {
      ...basicStats,
      heapUsedBytes: memory?.usedJSHeapSize,
      heapTotalBytes: memory?.totalJSHeapSize,
      heapLimitBytes: memory?.jsHeapSizeLimit,
    };
  }

  /*
   */
  public logStats(): void {
    const stats = this.getStatsWithHeap();
    const toKB = (v?: number) => (v ? (v / 1024).toFixed(2) + ' KB' : 'N/A');

    console.log(
      `${this._TAG} __STATS: count=${stats.count}, cacheSize=${toKB(stats.estimatedSizeBytes)}, heapUsed=${toKB(
        stats.heapUsedBytes,
      )}, heapLimit=${toKB(stats.heapLimitBytes)}`,
    );
  }
}
